/**
 * Lead Queue — prioritized call list for sales reps.
 *
 * Shows the next leads to call, ordered by priority (SLA, segment, recency).
 * "Call Next" button auto-picks the highest-priority lead and navigates to VoIP.
 * Tabs: Up Next, Callbacks, Retry Later, Parked.
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Phone, PhoneCall, Clock, SkipForward, CalendarClock, Archive, RotateCcw, AlertTriangle, Zap } from 'lucide-react'
import { queueApi, type QueueItem, type QueueStats } from '../../api/endpoints'
import { StatusBadge, StageBadge, SegmentBadge } from '../../components/ui/Badge'
import Pagination from '../../components/ui/Pagination'
import EmptyState from '../../components/ui/EmptyState'
import { fmtRelative } from '../../utils/date'
import { useScoringSegments } from '../../hooks/usePipelineStages'

type Tab = 'up_next' | 'callbacks' | 'retry_later' | 'parked'

const TABS: { id: Tab; label: string; icon: typeof Phone }[] = [
  { id: 'up_next', label: 'Up Next', icon: Zap },
  { id: 'callbacks', label: 'Callbacks', icon: CalendarClock },
  { id: 'retry_later', label: 'Retry Later', icon: Clock },
  { id: 'parked', label: 'Parked', icon: Archive },
]

const PAGE_SIZE = 30

const SEGMENT_COLOR_FALLBACK: Record<string, string> = {
  HOT: '#ef4444', WARM: '#f59e0b', COLD: '#3b82f6',
}

export default function QueuePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<Tab>('up_next')
  const [offset, setOffset] = useState(0)

  const scoringSegments = useScoringSegments()
  const segmentColorMap = useMemo(() => {
    if (scoringSegments.length > 0) {
      return scoringSegments.reduce<Record<string, string>>((acc, s) => {
        if (s.color) acc[s.segment] = s.color
        return acc
      }, {})
    }
    return SEGMENT_COLOR_FALLBACK
  }, [scoringSegments])

  // Fetch queue
  const params: Record<string, string> = {
    tab: activeTab,
    limit: String(PAGE_SIZE),
    offset: String(offset),
  }
  const { data: queueData, isLoading } = useQuery({
    queryKey: ['queue', activeTab, offset],
    queryFn: () => queueApi.list(params),
    refetchInterval: 30000, // 30s polling
  })
  const items: QueueItem[] = queueData?.data || []
  const total = queueData?.total || 0

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['queue-stats'],
    queryFn: () => queueApi.stats(),
    refetchInterval: 30000,
  })
  const stats: QueueStats = statsData?.data || {
    up_next: 0, overdue_callbacks: 0, sla_breached: 0, sla_warning: 0,
    parked: 0, calls_today: 0, connects_today: 0, connect_rate: 0,
  }

  // Skip mutation
  const skipMutation = useMutation({
    mutationFn: (leadId: number) => queueApi.skip(leadId, 4),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['queue'] }),
  })

  // Park mutation
  const parkMutation = useMutation({
    mutationFn: (leadId: number) => queueApi.park(leadId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['queue'] }),
  })

  // Unpark mutation
  const unparkMutation = useMutation({
    mutationFn: (leadId: number) => queueApi.unpark(leadId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['queue'] }),
  })

  const handleCallNext = () => {
    if (items.length > 0) {
      navigate(`/voip?lead=${items[0].id}`)
    }
  }

  const handleCall = (item: QueueItem) => {
    navigate(`/voip?lead=${item.id}`)
  }

  const handleSkip = (item: QueueItem) => {
    skipMutation.mutate(item.id)
  }

  const handlePark = (item: QueueItem) => {
    parkMutation.mutate(item.id)
  }

  const handleUnpark = (item: QueueItem) => {
    unparkMutation.mutate(item.id)
  }

  const switchTab = (tab: Tab) => {
    setActiveTab(tab)
    setOffset(0)
  }

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <PhoneCall size={22} className="text-indigo-500" />
          <h1 className="text-2xl font-bold text-gray-900">Lead Queue</h1>
        </div>
      </div>

      {/* KPI Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <KpiCard label="Up Next" value={stats.up_next} color="indigo" />
        <KpiCard label="Calls Today" value={stats.calls_today} color="blue" />
        <KpiCard label="Connects" value={stats.connects_today} color="green" />
        <KpiCard label="Connect Rate" value={`${stats.connect_rate}%`} color="emerald" />
        <KpiCard label="SLA Breached" value={stats.sla_breached} color={stats.sla_breached > 0 ? 'red' : 'gray'} />
        <KpiCard label="Parked" value={stats.parked} color="gray" />
      </div>

      {/* Call Next Button */}
      {activeTab === 'up_next' && items.length > 0 && (
        <button
          onClick={handleCallNext}
          className="w-full mb-6 py-4 px-6 bg-green-600 hover:bg-green-700 text-white font-semibold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3"
        >
          <Phone size={22} />
          Call Next — {items[0].name}
          {items[0].quality_segment && (
            <span
              className="px-2 py-0.5 rounded text-xs font-bold text-white"
              style={{ backgroundColor: segmentColorMap[items[0].quality_segment] || '#6b7280' }}
            >
              {items[0].quality_segment}
            </span>
          )}
        </button>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg">
        {TABS.map(({ id, label, icon: Icon }) => {
          const count = id === 'up_next' ? stats.up_next
            : id === 'callbacks' ? stats.overdue_callbacks
            : id === 'parked' ? stats.parked : 0
          return (
            <button
              key={id}
              onClick={() => switchTab(id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-md transition-colors ${
                activeTab === id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={14} />
              {label}
              {count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  id === 'callbacks' && count > 0 ? 'bg-red-100 text-red-700' :
                  'bg-gray-200 text-gray-600'
                }`}>
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Queue List */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading queue...</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={activeTab === 'parked' ? Archive : PhoneCall}
          title={
            activeTab === 'up_next' ? 'Queue is clear!' :
            activeTab === 'callbacks' ? 'No callbacks scheduled' :
            activeTab === 'retry_later' ? 'No leads waiting for retry' :
            'No parked leads'
          }
          description={
            activeTab === 'up_next' ? 'All leads are either contacted or scheduled for later. Great work!' :
            activeTab === 'parked' ? 'Leads are auto-parked after 6 failed contact attempts.' :
            'Check back later.'
          }
        />
      ) : (
        <div className="space-y-2">
          {items.map((item, index) => (
            <QueueCard
              key={item.id}
              item={item}
              rank={offset + index + 1}
              onCall={() => handleCall(item)}
              onViewLead={() => navigate(`/leads/${item.id}`)}
              onSkip={activeTab !== 'parked' ? () => handleSkip(item) : undefined}
              onPark={activeTab !== 'parked' ? () => handlePark(item) : undefined}
              onUnpark={activeTab === 'parked' ? () => handleUnpark(item) : undefined}
            />
          ))}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="mt-4">
          <Pagination total={total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KpiCard({ label, value, color }: { label: string; value: number | string; color: string }) {
  const colorMap: Record<string, string> = {
    indigo: 'bg-indigo-50 text-indigo-700',
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    red: 'bg-red-50 text-red-700',
    gray: 'bg-gray-50 text-gray-600',
  }
  return (
    <div className={`rounded-xl p-3 ${colorMap[color] || colorMap.gray}`}>
      <p className="text-[11px] font-medium uppercase tracking-wider opacity-70">{label}</p>
      <p className="text-xl font-bold mt-0.5">{value}</p>
    </div>
  )
}

function QueueCard({
  item,
  rank,
  onCall,
  onViewLead,
  onSkip,
  onPark,
  onUnpark,
}: {
  item: QueueItem
  rank: number
  onCall: () => void
  onViewLead: () => void
  onSkip?: () => void
  onPark?: () => void
  onUnpark?: () => void
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:border-indigo-200 transition-colors">
      <div className="flex items-start gap-3">
        {/* Rank */}
        <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-500">
          {rank}
        </div>

        {/* Lead Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={onViewLead} className="font-semibold text-indigo-600 hover:text-indigo-800 hover:underline text-sm text-left">{item.name}</button>
            {item.quality_segment && <SegmentBadge segment={item.quality_segment} />}
            {item.stage && <StageBadge stage={item.stage} />}
            {item.sla_status === 'breached' && (
              <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded flex items-center gap-0.5">
                <AlertTriangle size={10} /> SLA BREACHED
              </span>
            )}
            {item.sla_status === 'warning' && (
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded">SLA WARNING</span>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
            {item.company && <span>{item.company}</span>}
            {item.phone_number && <span className="font-mono">{item.phone_number}</span>}
            {item.score != null && <span>Score: {Math.round(item.score)}</span>}
          </div>

          {/* Last call info */}
          <div className="flex items-center gap-3 text-xs text-gray-400 mt-1 flex-wrap">
            {item.last_call_date ? (
              <>
                <span>Last call: {fmtRelative(item.last_call_date)}</span>
                {item.last_call_status && <StatusBadge status={item.last_call_status} />}
                {item.last_call_duration != null && item.last_call_duration > 0 && (
                  <span>{Math.floor(item.last_call_duration / 60)}m {item.last_call_duration % 60}s</span>
                )}
              </>
            ) : (
              <span className="text-amber-500 font-medium">Never contacted</span>
            )}
            {(item.contact_attempts ?? item.call_count) > 0 && (
              <span>{item.contact_attempts ?? item.call_count} attempt{(item.contact_attempts ?? item.call_count) !== 1 ? 's' : ''}</span>
            )}
            {item.is_parked && item.parked_at && (
              <span className="text-gray-400">Parked {fmtRelative(item.parked_at)}{(item as any).parked_by_name ? ` by ${(item as any).parked_by_name}` : ''}{item.park_reason ? ` (${item.park_reason})` : ''}</span>
            )}
          </div>

          {/* Priority reason */}
          {item.reason && (
            <p className="text-[11px] text-indigo-600 font-medium mt-1">{item.reason}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={onCall}
            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors"
          >
            <Phone size={12} /> Call
          </button>
          {onSkip && (
            <button
              onClick={onSkip}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium rounded-lg transition-colors"
              title="Skip — retry in 4 hours"
            >
              <SkipForward size={12} /> Skip
            </button>
          )}
          {onPark && (
            <button
              onClick={onPark}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-500 text-xs font-medium rounded-lg transition-colors"
              title="Park — remove from active queue"
            >
              <Archive size={12} /> Park
            </button>
          )}
          {onUnpark && (
            <button
              onClick={onUnpark}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-medium rounded-lg transition-colors"
              title="Return to active queue"
            >
              <RotateCcw size={12} /> Unpark
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
